import { useState } from 'react';
import { StatusDot, token } from '@bp-files/ui';
import { EXPORT_BUTTON_COPY } from '@/components/ExportButton/exportButton.copy';
import { messageFrom } from '@bp-files/client';
import { saveExport } from '@/lib/saveExport';
import type { ExportEnvelope } from '@/lib/api';

/**
 * The control behind `POST /exports/manifest` and `POST /exports/audit`
 * (SPEC §19; EXIT-5, EXIT-6, §5.3).
 *
 * **One component with two callers**, because what happens after the click is
 * one behaviour: request, write two files, say what happened. The Storage
 * surface exports the manifest and the Audit surface exports the log — each
 * sitting on the surface whose data it describes, which is where an admin is
 * already looking when they want it.
 *
 * Three things about it are deliberate.
 *
 * **One click writes both files.** EXIT-5 delivers the manifest "as CSV and
 * JSON" and the API returns both renderings of one walk, so there is no format
 * to choose — offering one would put a decision in front of a reader who needs
 * both, and would invite the two-requests-two-instants problem the API's shape
 * exists to prevent.
 *
 * **Neutral styling, not the blue glow.** The design system spends that on one
 * primary act per surface, and neither of these is it — Storage's is nothing at
 * all and Audit's is its filter.
 *
 * **It is visibility, not authorization.** The API refuses a non-admin caller on
 * every request whatever this renders (SEC-5, §5.3).
 */

type ExportState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'done'; rows: number }
  | { kind: 'error'; error: unknown };

export interface ExportButtonProps {
  /** `manifest` or `audit` — names the written files and nothing else. */
  kind: string;
  /** What this export *is*, in the caller's words. */
  label: string;
  request: () => Promise<ExportEnvelope>;
}

export function ExportButton({ kind, label, request }: ExportButtonProps) {
  const [state, setState] = useState<ExportState>({ kind: 'idle' });

  function run(): void {
    setState({ kind: 'pending' });

    request()
      .then((envelope) => {
        saveExport(kind, envelope);
        setState({ kind: 'done', rows: envelope.rowCount });
      })
      .catch((error: unknown) => {
        setState({ kind: 'error', error });
      });
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        onClick={run}
        disabled={state.kind === 'pending'}
        // `min-h-9`, `text-sm`: the same target this workspace's other
        // header-row buttons carry — `RecycleBinButton`, Spaces' Connect
        // control — not the smaller in-body sizing this control used to keep
        // for itself. Both its callers now sit in a `SurfaceHeader`'s
        // `actions` slot (Audit) or beside one (Storage's manifest section),
        // so one size reads as one family of control rather than two.
        className="inline-flex min-h-9 w-fit shrink-0 items-center justify-center border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60"
        style={{
          borderColor: token('bp-border-strong'),
          color: token('bp-text'),
          borderRadius: token('radius-md'),
        }}
      >
        {state.kind === 'pending' ? EXPORT_BUTTON_COPY.pending : label}
      </button>

      {/*
        A completed export is a `role="status"`, never an `alert` — nothing
        failed, and the line exists because two files landing from one click is
        the part a reader would otherwise wonder about.
      */}
      {state.kind === 'done' ? (
        <p role="status" className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {state.rows === 0 ? EXPORT_BUTTON_COPY.doneEmpty : EXPORT_BUTTON_COPY.done(state.rows)}
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <p role="alert" className="flex items-center gap-2 text-xs text-danger">
          <StatusDot tone="alert" />
          {messageFrom(state.error, EXPORT_BUTTON_COPY.error)}
        </p>
      ) : null}
    </div>
  );
}
